import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
    GraduationCap, 
    Check, 
    Video,
    PlayCircle,
    Clock,
    Plane,
    Star,
    Users,
    Lock,
    Play,
    ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

// Square payment link for the course
const SQUARE_COURSE_URL = "https://square.link/u/QEEPqkFI";

// Course videos - 10 YouTube videos
const COURSE_VIDEOS = [
    {
        id: 1,
        title: "How to Become a Flight Attendant",
        description: "Learn the fundamentals of starting your flight attendant career",
        youtubeId: "tGp3BNM-vRI",
        duration: "15:00"
    },
    {
        id: 2,
        title: "Best Resume to Get the Job",
        description: "Create a resume that stands out to airline recruiters",
        youtubeId: "TxuNXpT_8Fw",
        duration: "12:00"
    },
    {
        id: 3,
        title: "The Application Process",
        description: "Step-by-step guide to applying for flight attendant positions",
        youtubeId: "0Tcxg9zXkvI",
        duration: "10:00"
    },
    {
        id: 4,
        title: "Appearances and Assessment",
        description: "How to present yourself professionally during assessment day",
        youtubeId: "i7mmTWLYv-o",
        duration: "14:00"
    },
    {
        id: 5,
        title: "How to Answer Interview Questions",
        description: "Master the art of answering airline interview questions",
        youtubeId: "z30Fm1fzlDI",
        duration: "18:00"
    },
    {
        id: 6,
        title: "What to Expect in Training",
        description: "Prepare for what comes after you get hired",
        youtubeId: "i9Si08EUTzU",
        duration: "11:00"
    },
    {
        id: 7,
        title: "Graduated, Now What?",
        description: "Your first steps as a new flight attendant",
        youtubeId: "bC2zbLfL-JA",
        duration: "9:00"
    },
    {
        id: 8,
        title: "Answer Interview Questions Quick and Efficiently",
        description: "Advanced techniques for impressive interview responses",
        youtubeId: "AHUbI6wwQ7Q",
        duration: "13:00"
    },
    {
        id: 9,
        title: "Control Interview Nerves",
        description: "Overcome anxiety and perform your best",
        youtubeId: "5Cht7tRI-ck",
        duration: "8:00"
    },
    {
        id: 10,
        title: "Leave a Positive Lasting Impression",
        description: "End your interview on a high note",
        youtubeId: "vzyNeCv2RKw",
        duration: "10:00"
    }
];

export default function Course() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [showPreview, setShowPreview] = useState(false);
    const [previewEnded, setPreviewEnded] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [previewTimeLeft, setPreviewTimeLeft] = useState(120);
    const previewTimerRef = useRef(null);
    const countdownRef = useRef(null);

    // Preview duration: 2 minutes (120 seconds)
    const previewDuration = 120;

    // Check if user has course access
    const hasCourseAccess = user?.has_course_access;

    const handlePurchase = () => {
        window.open(SQUARE_COURSE_URL, '_blank');
    };

    const startPreview = () => {
        setShowPreview(true);
        setPreviewEnded(false);
        setPreviewTimeLeft(previewDuration);
        
        // Countdown timer
        countdownRef.current = setInterval(() => {
            setPreviewTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(countdownRef.current);
                    setPreviewEnded(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const stopPreview = () => {
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current);
        }
        if (countdownRef.current) {
            clearInterval(countdownRef.current);
        }
        setShowPreview(false);
        setPreviewEnded(false);
        setPreviewTimeLeft(previewDuration);
    };

    const openVideo = (video) => {
        if (!hasCourseAccess) {
            toast.error('Please purchase the course to watch this video');
            return;
        }
        setSelectedVideo(video);
    };

    useEffect(() => {
        return () => {
            if (previewTimerRef.current) {
                clearTimeout(previewTimerRef.current);
            }
            if (countdownRef.current) {
                clearInterval(countdownRef.current);
            }
        };
    }, []);

    // Format time as MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="min-h-screen py-12 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div 
                    className="text-center mb-12"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <Badge className="mb-4 bg-primary/10 text-primary">Video Course</Badge>
                    <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                        Flight Attendant Mastery Course
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        10 comprehensive video lessons to help you land your dream flight attendant job
                    </p>
                </motion.div>

                {/* Main Content */}
                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Course Info Card */}
                    <div className="lg:col-span-1">
                        <Card className="p-6 sticky top-24">
                            <div className="aspect-video bg-gradient-to-br from-stone-100 to-stone-200 dark:from-stone-800 dark:to-stone-900 rounded-lg mb-4 flex items-center justify-center relative overflow-hidden">
                                <GraduationCap className="h-16 w-16 text-primary/50" />
                                {!hasCourseAccess && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                        <Button 
                                            variant="secondary" 
                                            className="gap-2"
                                            onClick={startPreview}
                                        >
                                            <Play className="h-4 w-4" />
                                            Watch 2-Min Preview
                                        </Button>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <div className="flex items-baseline gap-2 mb-2">
                                        <span className="text-4xl font-bold text-primary">$297</span>
                                        <span className="text-muted-foreground">one-time</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Lifetime access to all 10 video lessons
                                    </p>
                                </div>

                                <div className="flex items-center gap-1">
                                    {[1,2,3,4,5].map(i => (
                                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    ))}
                                    <span className="text-sm text-muted-foreground ml-2">(500+ students)</span>
                                </div>

                                {hasCourseAccess ? (
                                    <Badge className="w-full justify-center py-2 bg-green-500 text-white">
                                        <Check className="h-4 w-4 mr-2" />
                                        You Have Access
                                    </Badge>
                                ) : (
                                    <Button 
                                        className="w-full rounded-full gap-2"
                                        onClick={handlePurchase}
                                        data-testid="purchase-course-btn"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        Purchase Course - $297
                                    </Button>
                                )}

                                <div className="pt-4 border-t space-y-2">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Video className="h-4 w-4 text-primary" />
                                        <span>10 Video Lessons</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Clock className="h-4 w-4 text-primary" />
                                        <span>2+ Hours of Content</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Users className="h-4 w-4 text-primary" />
                                        <span>Learn at Your Own Pace</span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Video List */}
                    <div className="lg:col-span-2">
                        <h2 className="font-heading text-2xl font-bold mb-6">Course Curriculum</h2>
                        <div className="space-y-3">
                            {COURSE_VIDEOS.map((video, index) => (
                                <motion.div
                                    key={video.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card 
                                        className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                                            hasCourseAccess ? 'hover:border-primary' : 'opacity-80'
                                        }`}
                                        onClick={() => openVideo(video)}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`h-12 w-12 rounded-lg flex items-center justify-center shrink-0 ${
                                                hasCourseAccess 
                                                    ? 'bg-primary/10' 
                                                    : 'bg-muted'
                                            }`}>
                                                {hasCourseAccess ? (
                                                    <PlayCircle className="h-6 w-6 text-primary" />
                                                ) : (
                                                    <Lock className="h-5 w-5 text-muted-foreground" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-medium text-muted-foreground">
                                                        Lesson {video.id}
                                                    </span>
                                                    {video.id === 1 && !hasCourseAccess && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            Preview Available
                                                        </Badge>
                                                    )}
                                                </div>
                                                <h3 className="font-semibold truncate">{video.title}</h3>
                                                <p className="text-sm text-muted-foreground truncate">
                                                    {video.description}
                                                </p>
                                            </div>
                                            <div className="text-sm text-muted-foreground shrink-0">
                                                {video.duration}
                                            </div>
                                        </div>
                                    </Card>
                                </motion.div>
                            ))}
                        </div>

                        {/* What You'll Learn */}
                        <Card className="p-6 mt-8 bg-gradient-to-br from-stone-50 to-stone-100 dark:from-stone-900 dark:to-stone-800">
                            <h3 className="font-heading text-xl font-bold mb-4">What You'll Learn</h3>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {[
                                    "How to start your FA career",
                                    "Create a winning resume",
                                    "Navigate the application process",
                                    "Ace your interview",
                                    "Master assessment day",
                                    "Control interview nerves",
                                    "Answer questions effectively",
                                    "Leave lasting impressions",
                                    "What to expect in training",
                                    "Succeed as a new FA"
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <Check className="h-4 w-4 text-green-500 shrink-0" />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>

                {/* Preview Modal */}
                <Dialog open={showPreview} onOpenChange={stopPreview}>
                    <DialogContent className="max-w-4xl">
                        <DialogHeader>
                            <DialogTitle className="flex items-center justify-between">
                                <span>Course Preview</span>
                                {!previewEnded && (
                                    <span className="text-sm font-normal bg-red-500 text-white px-3 py-1 rounded-full animate-pulse">
                                        {formatTime(previewTimeLeft)} remaining
                                    </span>
                                )}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="aspect-video bg-black rounded-lg overflow-hidden relative">
                            {previewEnded ? (
                                <div className="w-full h-full flex flex-col items-center justify-center bg-stone-900 text-white">
                                    <Lock className="h-12 w-12 mb-4 text-stone-400" />
                                    <h3 className="text-xl font-semibold mb-2">Preview Ended</h3>
                                    <p className="text-stone-400 mb-4">Purchase the course to continue watching</p>
                                    <Button onClick={handlePurchase} className="gap-2">
                                        <ExternalLink className="h-4 w-4" />
                                        Get Full Access - $297
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`https://www.youtube.com/embed/${COURSE_VIDEOS[0].youtubeId}?autoplay=1&start=0&end=120&modestbranding=1&rel=0`}
                                        title="Course Preview"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    />
                                    {/* Overlay to prevent seeking past 2 minutes */}
                                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
                                </>
                            )}
                        </div>
                        {!previewEnded && (
                            <p className="text-center text-sm text-muted-foreground">
                                This preview is limited to 2 minutes. Purchase the course for full access to all 10 lessons.
                            </p>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Video Player Modal */}
                <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
                    <DialogContent className="max-w-4xl">
                        <DialogHeader>
                            <DialogTitle>{selectedVideo?.title}</DialogTitle>
                        </DialogHeader>
                        <div className="aspect-video bg-black rounded-lg overflow-hidden">
                            {selectedVideo && (
                                <iframe
                                    width="100%"
                                    height="100%"
                                    src={`https://www.youtube.com/embed/${selectedVideo.youtubeId}?autoplay=1`}
                                    title={selectedVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            )}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}
